import { getMetadata } from "../services/metadataService";
import { getUsers } from "../services/userService";
import { setToCache, getFromCache } from "./cacheManager";
import { Metadata, User } from "../types";

const PRELOAD_CACHE_KEYS = {
  METADATA: "preload:metadata",
  USERS: "preload:users",
  STATUSES: "preload:statuses",
} as const;

const PRELOAD_EXPIRY = 24 * 60 * 60 * 1000;

export const preloadCriticalData = async (): Promise<void> => {
  try {
    const token = localStorage.getItem("authToken");
    const userJson = localStorage.getItem("user");

    if (!token || !userJson) {
      console.log("No auth token, skipping preload");
      return;
    }

    const cachedMetadata = await getFromCache<Metadata>(
      PRELOAD_CACHE_KEYS.METADATA
    );
    const cachedUsers = await getFromCache<User[]>(PRELOAD_CACHE_KEYS.USERS);

    if (cachedMetadata && cachedUsers && cachedUsers.length > 0) {
      console.log("Using cached critical data");
      return;
    }

    if (!navigator.onLine) {
      console.log("Offline mode: using cached data if available");
      return;
    }

    const promises: Promise<any>[] = [];

    if (!cachedMetadata) {
      promises.push(
        getMetadata()
          .then((metadata) => {
            setToCache(PRELOAD_CACHE_KEYS.METADATA, metadata, PRELOAD_EXPIRY);
            const taskStatuses = metadata.entityDefs?.Task?.statusList || [];
            const meetingStatuses =
              metadata.entityDefs?.Meeting?.statusList || [];
            const projectStatuses =
              metadata.entityDefs?.Project?.fields?.status?.options || [];
            setToCache(
              PRELOAD_CACHE_KEYS.STATUSES,
              {
                tasks: taskStatuses,
                meetings: meetingStatuses,
                projects: projectStatuses,
              },
              PRELOAD_EXPIRY
            );
            return metadata;
          })
          .catch((err) => {
            if (navigator.onLine) {
              console.warn("Failed to preload metadata:", err);
            }
          })
      );
    }

    if (!cachedUsers) {
      promises.push(
        getUsers(0, 100)
          .then((users) => {
            setToCache(PRELOAD_CACHE_KEYS.USERS, users, PRELOAD_EXPIRY);
            return users;
          })
          .catch((err) => {
            if (navigator.onLine) {
              console.warn("Failed to preload users:", err);
            }
          })
      );
    }

    if (promises.length > 0) {
      await Promise.all(promises);
      console.log("Critical data preloaded successfully");
    }
  } catch (error) {
    if (navigator.onLine) {
      console.error("Error preloading critical data:", error);
    }
  }
};

export const getCachedStatuses = async (): Promise<{
  tasks: string[];
  meetings: string[];
  projects: string[];
}> => {
  let metadata = await getFromCache<Metadata>("metadata:app");

  if (!metadata) {
    try {
      const stored = localStorage.getItem("metadata");
      if (stored) {
        metadata = JSON.parse(stored);
      }
    } catch (err) {
      console.error("Failed to parse metadata from localStorage:", err);
    }
  }

  if (metadata) {
    const projectStatuses =
      metadata.entityDefs?.Project?.fields?.status?.options;
    const result = {
      tasks: metadata.entityDefs?.Task?.statusList || [],
      meetings: metadata.entityDefs?.Meeting?.statusList || [],
      projects: projectStatuses || [],
    };
    await setToCache(PRELOAD_CACHE_KEYS.STATUSES, result, PRELOAD_EXPIRY);
    return result;
  }

  const cached = await getFromCache<{
    tasks?: string[];
    meetings?: string[];
    projects?: string[];
  }>(PRELOAD_CACHE_KEYS.STATUSES);
  if (cached) {
    return {
      tasks: cached.tasks || [],
      meetings: cached.meetings || [],
      projects: cached.projects || [],
    };
  }

  return {
    tasks: ["Not Started", "Started", "Completed", "Deferred", "Canceled"],
    meetings: ["Planned", "Held", "Not Held"],
    projects: [],
  };
};

export const getCachedUsers = async (): Promise<User[]> => {
  const cached = await getFromCache<User[]>(PRELOAD_CACHE_KEYS.USERS);
  if (cached) {
    return cached;
  }

  const token = localStorage.getItem("authToken");
  const userJson = localStorage.getItem("user");

  if (!token || !userJson) {
    return [];
  }

  if (navigator.onLine) {
    try {
      const users = await getUsers(0, 100);
      await setToCache(PRELOAD_CACHE_KEYS.USERS, users, PRELOAD_EXPIRY);
      return users;
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  }

  return [];
};

export const getCachedMetadata = async (): Promise<Metadata | null> => {
  return getFromCache<Metadata>(PRELOAD_CACHE_KEYS.METADATA);
};

export const isCriticalDataLoaded = async (): Promise<boolean> => {
  const metadata = await getFromCache<Metadata>(PRELOAD_CACHE_KEYS.METADATA);
  const users = await getFromCache<User[]>(PRELOAD_CACHE_KEYS.USERS);
  return !!(metadata && users && users.length > 0);
};


